import { getTokenSync } from '@/lib/oidc-client';
import { forward, sample } from 'effector';
import type { Event } from 'effector';
import { debounce } from 'patronum/debounce';
import { authDomain, domainKey, UPDATE_INTERVAL } from './constants';
import { checkToken, decodeToken, ValidationStatus, checkTokenUpdateSetting } from './jwt-helpers';
import { interval } from 'patronum/interval';
import {
  authFailed,
  authRequested,
  authSuccessful,
  handleError,
  userSignout,
  userSignoutFailed,
  userSignoutSuccessful
} from './events';
import { signoutFx, updateExpiredTokenFx, updateInvalidTokenFx, removeTokenFx } from './effects';

interface IAuthState {
  user: Nullable<IUser>;
  isAuth: Nullable<boolean>;
  error: Nullable<IFeError>;
}

export const initialAuthState = {
  user: null,
  isAuth: null,
  error: null
};

const isTokenUpdateEnabled = checkTokenUpdateSetting();
// Store
export const $auth = authDomain.createStore<IAuthState>(initialAuthState, {
  name: `${domainKey}/stores/auth`,
  sid: `${domainKey}/stores/auth`
});

const $user = authDomain.createStore<Nullable<IUser>>(null, {
  name: `${domainKey}/stores/user`,
  sid: `${domainKey}/stores/user`
});

// Signout

forward({
  from: userSignout,
  to: signoutFx
});

sample({
  clock: signoutFx.fail,
  source: $auth,
  fn: (store, error) => ({ ...store, error }),
  target: userSignoutFailed
});

sample({
  clock: signoutFx.finally,
  source: $auth,
  filter: (store) => !store.error,
  target: userSignoutSuccessful
});

$auth.on(userSignoutSuccessful, (value) => ({ ...value, isAuth: null, user: null }));

// Authentication

const authRequestedDebounced = debounce({ source: authRequested, timeout: 50 });
updateTokenRunner(authRequestedDebounced, userSignout);

$auth
  .on(handleError, (state, payload) => ({ ...state, error: payload }))
  .on(authSuccessful, (state, payload) => ({ ...state, error: null, isAuth: true, user: payload }))
  .on(authFailed, (state) => ({ ...state, isAuth: false }));

// create token store
const $token = authDomain
  .createStore('', {
    name: `${domainKey}/stores/token`,
    sid: `${domainKey}/stores/token`
  })
  .on(authRequestedDebounced, getTokenSync);

// create validity store
const $validity = $token.map((token) => checkToken(token, handleError));

// check validity
sample({
  source: $token,
  clock: authRequestedDebounced,
  fn: (token) => checkToken(token, handleError),
  target: $validity
});

//// get user information, throw authSuccessful event

sample({
  source: { token: $token, validity: $validity },
  filter: ({ validity }) => validity === ValidationStatus.valid,
  fn: ({ token }) => {
    // const { email = '', given_name = '', family_name = '' , sub = '' } = decodeToken<Record<string, unknown>>(token);
    const decoded = decodeToken<Record<string, unknown>>(token);
    return {
      email: decoded.email as string,
      given_name: decoded.given_name as string,
      family_name: decoded.family_name as string,
      sub: decoded.sub as string
    };
  },
  target: [$user, authSuccessful]
});

//// handle errors

sample({
  source: { token: $token, validity: $validity },
  filter({ validity }) {
    if (window.location.pathname === '/login') {
      return false;
    }

    return validity === ValidationStatus.expired && isTokenUpdateEnabled;
  },
  fn: ({ token }) => token,
  target: updateExpiredTokenFx
});

sample({
  source: { token: $token, validity: $validity },
  filter({ validity }) {
    return validity === ValidationStatus.expired && !isTokenUpdateEnabled;
  },
  target: signoutFx
});

sample({
  source: $validity,
  filter: (validity) => validity === ValidationStatus.failed,
  target: updateInvalidTokenFx
});

$token.on(removeTokenFx, () => '');

// Check token every UPDATE_INTERVAL milliseconds

function updateTokenRunner(toTrigger: Event<void>, toStop: Event<void>) {
  const startUpdateTokenCounterFx = authDomain.createEvent(
    `${domainKey}/events/startUpdateTokenCounterFx`
  );
  const stopUpdateTokenCounterFx = authDomain.createEvent(
    `${domainKey}/events/stopUpdateTokenCounterFx`
  );

  const { tick } = interval({
    timeout: UPDATE_INTERVAL,
    start: startUpdateTokenCounterFx,
    stop: stopUpdateTokenCounterFx
  });
  startUpdateTokenCounterFx();

  tick.watch(() => {
    toTrigger();
  });
  toStop.watch(() => stopUpdateTokenCounterFx());
}
